/**
 * Features is the same for both domain and one-core. It could be merged with commonTypes, but separating it made a little more order
 */
import { AnalyticsEventType, PluginType } from '@accedo/one-plugin-sdk';

import { AuthenticationType } from '#/client';

export type FeatureConfig<TProps> = {
  enabled: boolean;
  key: string;
  plugin?: {
    type: PluginType;
    id: string;
    src: string;
  };
  properties: TProps;
};

export type FeatureArrayData<TProps> = FeatureConfig<TProps>[];

// TODO: Adjust usage of auth plugins to make it compatible with other features (i.e. remove this at some point and use basic FeatureConfig<TProps>)
type AuthFeatureData<TProps> = FeatureConfig<TProps> & {
  type?: AuthenticationType;
};
export type AuthFeatureArrayData<TProps> = AuthFeatureData<TProps>[];

type DateIntervalType = {
  past: number;
  future: number;
};

export type FeatureScheduleProps = {
  channelCornerRadius: number;
  channelImage: string;
  channelTitle: string;
  channelPlaybackUrl: string;
  dateSelectorDateInterval: DateIntervalType;
  dateSelectorFormat: string;
  scheduleDescription: string;
  scheduleTimeFormat: string;
  scheduleTitle: string;
  scheduleSubTitle: string;
  scheduleVisibleUpcoming: number;
  scheduleMaxPages?: number;
  channelImageTransformations?: string[];
  channelOverviewEnabled: boolean;
  useProgramGuideV2: boolean;
};

export type FeatureSearchProps = {
  placeholder: string;
  preSearchPageId: string;
  autoSearchDelayMs: number;
  minSearchLength: number;
};

export type FeaturePlayerProps = {
  // all players can have these
  subtitleTypeFilter?: string;
  useNewControls?: boolean;
  // any player can have anything else
  [key: string]: any;
};

export type FeatureAnalyticsProps = {
  showDebugLogs?: boolean;
  events?: { [key in AnalyticsEventType]?: string[] };
  valueOverrides?: { [key: string]: any };
  // There are plugin specific other properties
  [key: string]: any;
};

export type FeaturePromotionProps = {
  type: string;
  route: string;
  planId: string;
  voucherBurnerPlanId?: string;
  backgroundImageUrl?: string;
  campaignCode?: string;
};

export type FeatureAdsProps = {
  keyValues?: { [key: string]: string };
  // There are plugin specific other properties
  [key: string]: any;
};

export type FeaturePageAdsProps = {};

export type FeaturePrivacyProps = {
  showDebugLogs?: boolean;
  disableFloatingButton?: boolean;
  // There are plugin specific other properties
  [key: string]: any;
};

export type FeaturePaywallProps = {
  sessionEndpoint?: string;

  // TODO: These are MPP specific, they shouldn't be here
  vendorTags?: string[];
  // There are plugin specific other properties
  [key: string]: any;
};

export type RawFeaturesConfig = {
  videoPlayer: FeatureArrayData<FeaturePlayerProps>;
  analytics: FeatureArrayData<FeatureAnalyticsProps>;
  promotions: FeatureArrayData<FeaturePromotionProps>;
  paywall: FeatureArrayData<FeaturePaywallProps>;
  schedule: FeatureArrayData<FeatureScheduleProps>;
  ads: FeatureArrayData<FeatureAdsProps>;
  pageAds: FeatureArrayData<FeaturePageAdsProps>;
  privacy: FeatureArrayData<FeaturePrivacyProps>;
};

export type FeaturesConfig = RawFeaturesConfig & {
  search: CoreSearchFeature;
  playback: CorePlaybackFeature;
  purchase: CorePurchaseFeature;
};

export interface CoreFeatureConfig {
  authentication: CoreAuthFeature;
  playback: CorePlaybackFeature;
  purchase: CorePurchaseFeature;
  search: CoreSearchFeature;
  unauthenticatedHomepage: CoreUnauthenticatedHomepageFeature;
  seo: CoreSeoFeature;
}

export interface CoreSeoFeature {
  defaultImage?: string;
  serviceTitle?: string;
}

export interface CorePlaybackFeature {
  /** @sample '12s' Time in between 2 playback beacons (sending progress and refreshing playback concurrency slots) */
  beaconInterval: string;

  stillWatching: {
    enabled: boolean;
    /** @sample '7200s' Time passed since the last user interaction. After this inactivity if the current playback reached the end the inactivity modal is shown to the user */
    inactivityThreshold: string;
  };
}

export interface CoreAuthFeature {
  secondScreen?: CoreAuthSecondScreen;
  standard?: CoreAuthStandard;
  tve?: CoreAuthTve;
}

export type CorePaymentMethod =
  | 'PAYMENT_METHOD_CREDIT_CARD'
  | 'PAYMENT_METHOD_PAYPAL'
  | 'PAYMENT_METHOD_UNKNOWN';

export interface CorePurchaseFeature {
  inApp: {
    enabled: boolean;
  };
  gifting?: {
    anonymousAllowed: boolean;
    enabled: boolean;
    images: {
      landingPageBackgroundImage?: string;
      planSelectionImage?: string;
    };
    purchase?: {
      allowedPaymentMethods: CorePaymentMethod[];
    };
    redemption?: {
      allowedPaymentMethods: CorePaymentMethod[];
    };
  };
  images?: {
    successfulPurchaseBackground?: string;
  };
}

export interface CoreSearchFeature {
  /** Search field behavior (search-as-you-go) */
  autoSubmit: {
    /** @sample '0.1s' Time to wait for the search to get submitted after the minimum search query length is met (debounce delay). */
    delay: string;

    /** Whether to automatically trigger search or not as the user types, or to wait for him to press the Search button. */
    enabled: boolean;

    /** The number of characters the user has to enter before the auto-search is triggered if enabled. */
    minimumSearchQueryLength: number;
  };
}

interface CoreUnauthenticatedHomepageFeature {
  enabled: boolean;
}

export enum ForgotPasswordSubmitBehavior {
  /** SPLIT - wait for click on link in email */
  Split = 'SPLIT',

  /** DIRECT_TO_UPDATE_PASSWORD_FORM - go to update password form and ask user to input password reset code on device */
  Direct = 'DIRECT_TO_UPDATE_PASSWORD_FORM',
}

interface CoreAuthStandard {
  enabled: boolean;
  forgotPassword?: {
    enabled: boolean;
    /** If true, forgot password flows can be completed on the device itself */
    onDevice: boolean;
    /** If set, the forgot password flow may be completed by sending the user to the specified URL. The client is expected to display the URL */
    onWeb?: {
      /** User-friendly (shorter) URL that can be displayed to the user, that may skip some optional parameters or https://www. prefixes. */
      displayUrl?: string;
      /** QR code image URL that encodes the 'url' to the form. */
      qrCodeUrl?: string;
      /** URL to the form that where the user can complete the authentication flow. */
      url?: string;
    };

    submitBehavior: ForgotPasswordSubmitBehavior;
  };
  login?: {
    enabled: boolean;
    /** If true, login can be completed on the device itself */
    onDevice: boolean;
    /** If true, login can be completed on a second screen device, by requesting a second screen code. */
    onSecondScreen: boolean;
  };
  registration?: {
    enabled: boolean;
    /** If true, registration can be completed on the device itself */
    onDevice: boolean;
  };
}

interface CoreAuthTve {
  enabled: boolean;
  /** If true, TVE authentication can be completed on the device itself (the form should be rendered on the device directly, and the SSO calls should be performed in the device's browser). */
  onDevice: boolean;
  /** If true, TVE authentication can be completed on a second screen device, by requesting a second screen code. */
  onSecondScreen: boolean;
}

interface CoreAuthSecondScreen {
  /** The length of the second-screen codes generated by one-core, so that the input field on the second screen device may be adjusted and pre-validated. */
  codeLength: number;
  enabled: boolean;
}

export interface CoreSearchFeature {
  /** Search field behavior (search-as-you-go) */
  autoSubmit: {
    /** @sample '0.1s' Time to wait for the search to get submitted after the minimum search query length is met (debounce delay). */
    delay: string;

    /** Whether to automatically trigger search or not as the user types, or to wait for him to press the Search button. */
    enabled: boolean;

    /** The number of characters the user has to enter before the auto-search is triggered if enabled. */
    minimumSearchQueryLength: number;
  };
}
